import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Link } from 'react-router-dom';
import {
  TypeCurveVerificationStatusIndicator,
  typeCurveVerificationIsDecided,
} from 'components/type-curve-verification-status';
import { parseLocalDateTime } from 'utils/dates';
import type { TypeCurveListingRow } from 'types/api-responses';
import typeCurveStyle from './type-curve-area-popup.module.scss';

/**
 * A type curve area's type curves as its map popup lists them: each Unique TC ID
 * links to its type curve, with its verification, who last verified it and when,
 * its formation and its status. Shared by the type curve area popup and Map
 * Query's TypeCurve areas.
 * @param {{ typeCurves: TypeCurveListingRow[]; height?: number | string; fitWidth?: boolean }} props
 *   The type curves, the container's height when the host sizes it, and whether it
 *   fits the host's width.
 * @return {JSX.Element} The table.
 */
export function TypeCurveLinksTable({
  typeCurves,
  height,
  fitWidth = false,
}: {
  typeCurves: TypeCurveListingRow[];
  /** The container's height, when the host sizes it to fit the map. */
  height?: number | string;
  /** Shrink to the host's width (and scroll sideways) instead of holding 600px. */
  fitWidth?: boolean;
}): JSX.Element {
  const style = {
    ...(height === undefined ? {} : { height }),
    ...(fitWidth ? { minWidth: 0 } : {}),
  };
  return (
    <div
      className={typeCurveStyle.tableContainer}
      style={Object.keys(style).length ? style : undefined}
      data-testid="type-curve-table-container"
    >
      <TableContainer className={typeCurveStyle.assetTable}>
        <Table
          aria-label="Type Curve Assets"
          size="sm"
          className={typeCurveStyle.fixedHeader}
        >
          <TableHead className={typeCurveStyle.stickyHeader}>
            <TableRow>
              <TableHeader>Unique TC ID</TableHeader>
              <TableHeader>Verification</TableHeader>
              <TableHeader>Last Verified By</TableHeader>
              <TableHeader>Last Verification Date</TableHeader>
              <TableHeader>Type Curve Formation</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody className={typeCurveStyle.tableBodyRow}>
            {typeCurves.map((tc) => {
              const typeCurveHref = tc.resources?.typeCurve?.href ?? tc.href;

              return (
                <TableRow key={tc.id} className={typeCurveStyle.tableBodyRow}>
                  <TableCell>
                    {typeCurveHref ? (
                      <Link
                        to={typeCurveHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={typeCurveStyle.visibleLink}
                      >
                        {tc.uniqueTcId}
                      </Link>
                    ) : (
                      tc.uniqueTcId
                    )}
                  </TableCell>
                  <TableCell>
                    {typeCurveVerificationIsDecided(tc.verification) ? (
                      <TypeCurveVerificationStatusIndicator
                        status={tc.verification}
                        showLabel
                        className={typeCurveStyle.verificationStatusCell}
                        iconClasses={{
                          correct: typeCurveStyle.verificationStatusCorrect,
                          incorrect: typeCurveStyle.verificationStatusIncorrect,
                        }}
                      />
                    ) : (
                      '--'
                    )}
                  </TableCell>
                  <TableCell>{tc.lastVerifiedBy || '--'}</TableCell>
                  <TableCell>
                    {parseLocalDateTime(tc.lastVerifiedDate ?? undefined) ||
                      '--'}
                  </TableCell>
                  <TableCell>{tc.formation || '--'}</TableCell>
                  <TableCell>{tc.status || '--'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
